import React from "react";

import { Editor } from "~/components/code/editor";

import { getRelationName } from "../../bottom_content/view/panes/east/element-details/ElementDetailsUtils";
import { TableRow } from "../../bottom_content/view/panes/east/element-details/modal-dialog/TableRow";
import type { FullComponent, FullRelation } from "../../common/openapi";
import { ExplorationDrawer } from "../react/drawer/ExplorationDrawer";
import { Tab, Tabs } from "../react/tabs/Tabs";

import { ElementDrawerTitle } from "./element-drawer-title";
import { GeneralPropertiesTabLayout } from "./general-properties-tab";
import { type SyncCode, useQuerySyncsCodeByElementId } from "./use-query-syncs-code-by-element-id";

export interface ElementPropertiesDrawerProps {
	when: undefined | FullComponent | FullRelation;
}

export const ElementPropertiesDrawer: React.FC<ElementPropertiesDrawerProps> = ({ when }) => (
	<ExplorationDrawer
		headerTitle={(fullElement) => <ElementDrawerTitle fullElement={fullElement} typeOfData="properties" />}
		when={when}
	>
		{(fullElement) => <ElementProperties fullElement={fullElement} />}
	</ExplorationDrawer>
);

const ElementProperties: React.FC<{ fullElement: FullComponent | FullRelation }> = ({ fullElement }) => {
	const syncDiffs = useQuerySyncsCodeByElementId({
		element: fullElement,
	});

	return (
		<Tabs className="h-full" data-testid="element-properties-tabs">
			<Tab key="general" tab="General">
				{"actions" in fullElement ? (
					<ComponentProperties fullComponent={fullElement} syncDiffs={syncDiffs} />
				) : (
					<RelationProperties fullRelation={fullElement} syncDiffs={syncDiffs} />
				)}
			</Tab>

			{syncDiffs?.map(({ name, code }, index) => (
				<Tab className="h-full" key={index} tab={name}>
					<Editor height="100%" language="yaml" readOnly value={code} />
				</Tab>
			))}
		</Tabs>
	);
};

const ComponentProperties: React.FC<{ fullComponent: FullComponent; syncDiffs: null | Array<SyncCode> }> = ({
	fullComponent,
	syncDiffs,
}) => (
	<GeneralPropertiesTabLayout
		componentTypeName={fullComponent.typeName}
		description={fullComponent.data.description}
		id={fullComponent.data.id}
		identifiers={fullComponent.data.identifiers}
		labels={fullComponent.data.tags}
		lastUpdateTimestamp={fullComponent.data.lastUpdateTimestamp}
		name={fullComponent.data.name}
		syncNames={syncDiffs?.map(({ name }) => name)}
		version={fullComponent.data.version}
	>
		<TableRow label="Layer">{fullComponent.data.layerName}</TableRow>

		<TableRow label="Domain">{fullComponent.data.domainName}</TableRow>
	</GeneralPropertiesTabLayout>
);

const RelationProperties: React.FC<{ fullRelation: FullRelation; syncDiffs: null | Array<SyncCode> }> = ({
	fullRelation,
	syncDiffs,
}) => (
	<GeneralPropertiesTabLayout
		componentTypeName={fullRelation.typeName}
		description={fullRelation.data.description}
		id={fullRelation.data.id}
		identifiers={fullRelation.data.identifiers}
		labels={fullRelation.data.tags}
		lastUpdateTimestamp={fullRelation.data.lastUpdateTimestamp}
		name={getRelationName(fullRelation, fullRelation.typeName)}
		syncNames={syncDiffs?.map(({ name }) => name)}
	/>
);
